import { useState, useEffect, useCallback, useRef } from "react";
import { io } from "socket.io-client";

// Get API URL from environment
import { BACKEND_URL } from "@/config/api";

/**
 * Hook for real-time trip collaboration features
 * Manages: viewing status, typing indicators, collaborative notes
 */
export const useCollaboration = (token, userName) => {
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [currentViewers, setCurrentViewers] = useState({}); // { itemId: [{ name, user_id, is_typing, editing_field }] }
  const [typingUsers, setTypingUsers] = useState({}); // { itemId: { user_id: { name, field, is_typing } } }
  const [collabNotes, setCollabNotes] = useState({}); // { itemId: [notes] }
  const currentItemRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Connect to WebSocket
  useEffect(() => {
    if (!token) return;

    const newSocket = io(BACKEND_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    newSocket.on('connect', () => {
      console.log('[Collab] WebSocket connected');
      setConnected(true);
    });

    newSocket.on('disconnect', () => {
      console.log('[Collab] WebSocket disconnected');
      setConnected(false);
    });

    // Someone joined viewing an item
    newSocket.on('collaborator_joined', (data) => {
      console.log('[Collab] Collaborator joined:', data);
      setCurrentViewers((prev) => ({
        ...prev,
        [data.item_id]: data.viewers || []
      }));
    });

    // Someone left viewing an item
    newSocket.on('collaborator_left', (data) => {
      console.log('[Collab] Collaborator left:', data);
      setCurrentViewers((prev) => {
        const viewers = (prev[data.item_id] || []).filter(v => v.user_id !== data.user_id);
        return { ...prev, [data.item_id]: viewers };
      });
    });

    // Current viewers when joining
    newSocket.on('current_viewers', (data) => {
      console.log('[Collab] Current viewers:', data);
      setCurrentViewers((prev) => ({
        ...prev,
        [data.item_id]: data.viewers || []
      }));
    });

    // Typing indicator
    newSocket.on('collaborator_typing', (data) => {
      console.log('[Collab] Typing update:', data);
      setTypingUsers((prev) => {
        const itemTyping = { ...(prev[data.item_id] || {}) };
        if (data.is_typing) {
          itemTyping[data.user_id] = {
            name: data.user_name,
            field: data.field,
            is_typing: true
          };
        } else {
          delete itemTyping[data.user_id];
        }
        return { ...prev, [data.item_id]: itemTyping };
      });
    });

    // New collaborative note
    newSocket.on('new_collab_note', (data) => {
      console.log('[Collab] New note:', data);
      setCollabNotes((prev) => ({
        ...prev,
        [data.item_id]: [...(prev[data.item_id] || []), data.note]
      }));
    });

    // Collaborative note deleted
    newSocket.on('collab_note_deleted', (data) => {
      console.log('[Collab] Note deleted:', data);
      setCollabNotes((prev) => ({
        ...prev,
        [data.item_id]: (prev[data.item_id] || []).filter(n => n.id !== data.note_id)
      }));
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, [token]);

  // Join an item (start viewing)
  const joinItem = useCallback((itemId) => {
    if (!socket || !connected || !itemId) return;
    
    // Leave previous item if any
    if (currentItemRef.current && currentItemRef.current !== itemId) {
      socket.emit('leave_item', { item_id: currentItemRef.current });
    }
    
    currentItemRef.current = itemId;
    socket.emit('join_item', { item_id: itemId, user_name: userName });
  }, [socket, connected, userName]);

  // Leave an item (stop viewing)
  const leaveItem = useCallback((itemId) => {
    if (!socket || !connected || !itemId) return;
    
    socket.emit('leave_item', { item_id: itemId });
    currentItemRef.current = null;
    
    // Clear local state for this item
    setCurrentViewers((prev) => {
      const { [itemId]: _, ...rest } = prev;
      return rest;
    });
    setTypingUsers((prev) => {
      const { [itemId]: _, ...rest } = prev;
      return rest;
    });
  }, [socket, connected]);

  // Start typing indicator
  const startTyping = useCallback((itemId, field = 'notes') => {
    if (!socket || !connected || !itemId) return;
    
    socket.emit('typing_start', { item_id: itemId, field, user_name: userName });
    
    // Auto-stop typing after 3 seconds of inactivity
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = setTimeout(() => {
      stopTyping(itemId);
    }, 3000);
  }, [socket, connected, userName]);

  // Stop typing indicator
  const stopTyping = useCallback((itemId) => {
    if (!socket || !connected || !itemId) return;
    
    socket.emit('typing_stop', { item_id: itemId, user_name: userName });
    
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
  }, [socket, connected, userName]);

  // Add a collaborative note
  const addNote = useCallback((itemId, text) => {
    if (!socket || !connected || !itemId || !text.trim()) return;
    
    socket.emit('add_collab_note', { item_id: itemId, text, user_name: userName });
  }, [socket, connected, userName]);

  // Delete a collaborative note
  const deleteNote = useCallback((itemId, noteId) => {
    if (!socket || !connected || !itemId || !noteId) return;
    
    socket.emit('delete_collab_note', { item_id: itemId, note_id: noteId });
  }, [socket, connected]);

  // Get viewers for a specific item
  const getViewers = useCallback((itemId) => {
    return currentViewers[itemId] || [];
  }, [currentViewers]);

  // Get typing users for a specific item
  const getTypingUsers = useCallback((itemId) => {
    return Object.values(typingUsers[itemId] || {});
  }, [typingUsers]);

  // Get collaborative notes for an item
  const getNotes = useCallback((itemId) => {
    return collabNotes[itemId] || [];
  }, [collabNotes]);

  // Set initial notes from API response
  const setInitialNotes = useCallback((itemId, notes) => {
    setCollabNotes((prev) => ({
      ...prev,
      [itemId]: notes || []
    }));
  }, []);

  return {
    connected,
    joinItem,
    leaveItem,
    startTyping,
    stopTyping,
    addNote,
    deleteNote,
    getViewers,
    getTypingUsers,
    getNotes,
    setInitialNotes,
    currentViewers,
    typingUsers,
  };
};

export default useCollaboration;
